import React, { FC, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomValidationConfig, CustomValidationConstraint, CustomValidationError } from "go-form/services/types";
import { TagsFormInput } from "../../../../../../../../../components/Tag/TagsFormInput";
import { StocktakingApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";
import StocktakingSummaryCard from "./StocktakingSummaryCard";

interface Props {
	stocktaking?: StocktakingApi;
	form: UseFormReturn<StocktakingApi>;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<StocktakingApi>[]>>;
}

const StocktakingBasicInfoForm: FC<Props> = ({ stocktaking, form, setErrors }) => {
	const { t } = useTranslation();
	const {
		control,
		formState: { errors },
		register,
	} = form;

	const onSearchStock = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getStockSearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const stocktakingTypeOptions = ["VALUE", "AVAILABILITY"].map((type) => ({
		label: t(`enums.stocktaking.type.${type}`),
		value: type,
	}));

	const stockIdValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const typeValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const stocktakingAtValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	return (
		<fieldset className="form-group">
			<div className="row">
				<div className={`col-lg-${stocktaking?.id ? "8" : "10"}`}>
					<div className="row">
						<div className="col-md-6">
							<FormSelectGroup
								control={control}
								loadOptions={onSearchStock}
								label={t("common.word.stock")}
								name="stock_id"
								isClearable={false}
								getOptionValue={(opt) => opt.id}
								defaultValue={{
									label: stocktaking?.stock?.name,
									id: stocktaking?.stock?.id,
								}}
								disabled={stocktaking?.stocktaking_status !== "DRAFT"}
								errors={errors}
								customValidationConfig={stockIdValidationConfig}
								data-testid="stock_id"
							/>
						</div>
						<div className="col-md-6">
							<FormSelectGroup
								control={control}
								options={stocktakingTypeOptions}
								label={t("lib:common.word.type")}
								name="type"
								isClearable={false}
								defaultValue={{
									label: t(`enums.stocktaking.type.${stocktaking?.type}`),
									value: stocktaking?.type,
								}}
								disabled={stocktaking?.stocktaking_status !== "DRAFT"}
								errors={errors}
								customValidationConfig={typeValidationConfig}
								data-testid="type"
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-md-6">
							<FormDatePicker
								errors={errors}
								control={control}
								name="stocktaking_at"
								disabled={
									!(
										stocktaking?.stocktaking_status === "DRAFT" ||
										stocktaking?.stocktaking_status === "OPEN"
									)
								}
								label={t("modules.stocktaking.field.stocktaking_at.title")}
								customValidationConfig={stocktakingAtValidationConfig}
							/>
						</div>
						<div className="col-md-6">
							<FormInput
								name="comment"
								errors={errors}
								register={register}
								label={t("common.word.comment.title")}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col">
							<TagsFormInput form={form} />
						</div>
					</div>
				</div>
				{stocktaking?.id && (
					<div className="col-lg-4 d-flex align-items-start item-summary-col">
						<StocktakingSummaryCard stocktaking={stocktaking} />
					</div>
				)}
			</div>
		</fieldset>
	);
};

export default StocktakingBasicInfoForm;
